import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatSizeFR, formatDateFR } from './DocumentsInternesPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token') || '';
const isAdmin = () => {
  try { return ['admin_agence', 'super_admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role || ''); }
  catch { return false; }
};

// Taxonomie des modèles agence (fixe, 6 types) + couleur sémantique du badge.
export const TEMPLATE_TYPES = [
  { id: 'bon_de_visite_type', label: 'Bon de visite type', badge: 'bg-blue-50 text-blue-700' },
  { id: 'mandat_type', label: 'Mandat type', badge: 'bg-emerald-50 text-emerald-700' },
  { id: 'etat_lieux_type', label: 'État des lieux type', badge: 'bg-amber-50 text-amber-700' },
  { id: 'compromis_type', label: 'Compromis type', badge: 'bg-slate-100 text-slate-700' },
  { id: 'lettre_intention_type', label: 'Lettre d\'intention type', badge: 'bg-cyan-50 text-cyan-700' },
  { id: 'autre', label: 'Autre', badge: 'bg-slate-100 text-slate-700' },
];
const typeMeta = (id) => TEMPLATE_TYPES.find((t) => t.id === id) || TEMPLATE_TYPES[5];

const TPL_ACCEPT = '.pdf,.docx,.xlsx';
const TPL_ALLOWED = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const MAX_BYTES = 25 * 1024 * 1024;

const mimeIc = (mime, name = '') => {
  const m = (mime || '').toLowerCase(); const n = (name || '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return 'bi-file-earmark-pdf text-rose-500';
  if (m.includes('word') || n.endsWith('.docx')) return 'bi-file-earmark-word text-blue-600';
  if (m.includes('sheet') || n.endsWith('.xlsx')) return 'bi-file-earmark-excel text-emerald-600';
  return 'bi-file-earmark text-slate-500';
};

// ─── Modale upload modèle ───
const TemplateUploadModal = ({ onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [nom, setNom] = useState('');
  const [type, setType] = useState('bon_de_visite_type');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!TPL_ALLOWED.has((f.type || '').toLowerCase())) { toast.error('Format non autorisé. Acceptés : PDF, DOCX, XLSX.'); return; }
    if (f.size > MAX_BYTES) { toast.error('Fichier trop volumineux (max 25 Mo).'); return; }
    setFile(f);
    if (!nom) setNom(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file) { toast.error('Sélectionnez un fichier.'); return; }
    if (!nom.trim()) { toast.error('Donnez un nom au modèle.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('nom_modele', nom.trim());
      fd.append('type_document', type);
      const r = await axios.post(`${API}/document-templates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Modèle ajouté.');
      onUploaded?.(r.data); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Échec de l\'envoi du modèle.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="template-upload-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800">Ajouter un modèle agence</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="template-upload-close"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
            className={`cursor-pointer border-2 border-dashed rounded-xl px-4 py-8 text-center transition ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-300 hover:bg-slate-50'}`}
            data-testid="template-upload-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700">
                <i className={`bi ${mimeIc(file.type, file.name)} text-xl`} />
                <span className="truncate max-w-[260px]">{file.name}</span>
                <span className="text-slate-400">· {formatSizeFR(file.size)}</span>
              </div>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up text-3xl text-slate-400" />
                <p className="text-sm text-slate-600 mt-1">Glissez un fichier ici ou <span className="text-blue-600 font-semibold">parcourez</span></p>
                <p className="text-[11px] text-slate-400 mt-0.5">PDF, DOCX, XLSX · 25 Mo max</p>
              </>
            )}
            <input ref={inputRef} type="file" accept={TPL_ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="template-upload-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nom du modèle</label>
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex : Bon de visite standard 2026" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="template-upload-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Type de modèle</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="template-upload-type">
              {TEMPLATE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={busy || !file} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2" data-testid="template-upload-submit">
            {busy ? <i className="bi bi-arrow-repeat animate-spin" /> : <i className="bi bi-cloud-upload" />}{busy ? 'Envoi…' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale renommage / type ───
const TemplateEditModal = ({ tpl, onClose, onSaved }) => {
  const [nom, setNom] = useState(tpl.nom_modele || '');
  const [type, setType] = useState(tpl.type_document || 'autre');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/document-templates/${tpl.id}`, { nom_modele: nom, type_document: type });
      toast.success('Modèle mis à jour.'); onSaved?.(r.data); onClose();
    } catch { toast.error('Échec de la mise à jour.'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="template-edit-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800">Modifier le modèle</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nom</label>
            <input value={nom} onChange={(e) => setNom(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="template-edit-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="template-edit-type">
              {TEMPLATE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg" data-testid="template-edit-save">{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Ligne modèle ───
const TemplateRow = ({ tpl, admin, onView, onDownload, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  const meta = typeMeta(tpl.type_document);
  return (
    <div className={`flex items-center gap-3 p-3 hover:bg-slate-50 transition ${tpl.actif === false ? 'opacity-60' : ''}`} data-testid={`template-row-${tpl.id}`}>
      <i className={`bi ${mimeIc(tpl.mime_type, tpl.filename_original)} text-2xl shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{tpl.nom_modele}{tpl.actif === false && <span className="ml-2 text-[10px] text-slate-400 font-normal">(désactivé)</span>}</p>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.badge}`}>{meta.label}</span>
          <span className="text-[11px] text-slate-400">{formatSizeFR(tpl.size_bytes)}</span>
          <span className="text-[11px] text-slate-400">· {formatDateFR(tpl.uploaded_at)}</span>
          {tpl.uploaded_by_name && <span className="text-[11px] text-slate-400">· {tpl.uploaded_by_name}</span>}
        </div>
      </div>
      <button onClick={() => onView(tpl)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-100 transition" title="Voir" data-testid={`template-view-${tpl.id}`}><i className="bi bi-eye" /></button>
      <button onClick={() => onDownload(tpl)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-100 transition" title="Télécharger" data-testid={`template-download-${tpl.id}`}><i className="bi bi-download" /></button>
      {admin && (
        <div className="relative" ref={ref}>
          <button onClick={() => setMenuOpen((v) => !v)} className="text-slate-500 hover:text-slate-800 p-1.5 rounded-md hover:bg-slate-100 transition" title="Plus" data-testid={`template-menu-${tpl.id}`}><i className="bi bi-three-dots-vertical" /></button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20">
              <button onClick={() => { setMenuOpen(false); onEdit(tpl); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid={`template-edit-${tpl.id}`}><i className="bi bi-pencil" /> Renommer / type</button>
              <button onClick={() => { setMenuOpen(false); onDelete(tpl); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid={`template-delete-${tpl.id}`}><i className="bi bi-trash" /> Supprimer</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentTemplatesLibrary = () => {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const admin = isAdmin();

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/document-templates`); setTpls(r.data || []); }
    catch { setTpls([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (typeFilter ? tpls.filter((t) => t.type_document === typeFilter) : tpls), [tpls, typeFilter]);

  const view = (t) => {
    const tk = getToken();
    if (!tk) { toast.error('Session expirée, veuillez vous reconnecter.'); return; }
    window.open(`${API}/document-templates/${t.id}/download?auth=${encodeURIComponent(tk)}`, '_blank', 'noopener');
  };
  const download = async (t) => {
    try {
      const r = await axios.get(`${API}/document-templates/${t.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = t.nom_modele || 'modele';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'Session expirée, veuillez vous reconnecter.' : 'Téléchargement impossible.');
    }
  };
  const del = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.nom_modele} » ?`)) return;
    try { await axios.delete(`${API}/document-templates/${t.id}`); setTpls((p) => p.filter((x) => x.id !== t.id)); toast.success('Modèle supprimé.'); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Suppression impossible.'); }
  };

  return (
    <div className="space-y-4" data-testid="document-templates">
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-2">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="templates-type-filter">
          <option value="">Tous les types</option>
          {TEMPLATE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <div className="flex-1" />
        {admin && (
          <button onClick={() => setShowUpload(true)} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition" data-testid="template-add-btn">
            <i className="bi bi-plus-lg" /> Ajouter un modèle
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><i className="bi bi-arrow-repeat animate-spin text-2xl" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl py-16 text-center" data-testid="templates-empty">
          <i className="bi bi-collection text-4xl text-slate-300" />
          <p className="text-sm font-medium text-slate-600 mt-2 max-w-md mx-auto">Aucun modèle agence. Ajoutez vos modèles types (bon de visite, mandat, état des lieux, compromis) pour les retrouver et les réutiliser facilement.</p>
          {admin && (
            <button onClick={() => setShowUpload(true)} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition" data-testid="templates-empty-add-btn">
              <i className="bi bi-plus-lg" /> Ajouter le premier modèle
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
          {filtered.map((t) => (
            <TemplateRow key={t.id} tpl={t} admin={admin} onView={view} onDownload={download} onEdit={setEditing} onDelete={del} />
          ))}
        </div>
      )}

      {showUpload && <TemplateUploadModal onClose={() => setShowUpload(false)} onUploaded={(t) => setTpls((p) => [t, ...p])} />}
      {editing && <TemplateEditModal tpl={editing} onClose={() => setEditing(null)} onSaved={(t) => setTpls((p) => p.map((x) => (x.id === t.id ? t : x)))} />}
    </div>
  );
};

export default DocumentTemplatesLibrary;
